import {
  Alert,
  Button,
  FileUpload,
  Flex,
  FlexItem,
  Form,
  FormGroup,
  PageSection,
  PageSectionVariants,
  Title,
} from '@patternfly/react-core'
import { useCallback, useState } from 'react'
import * as React from 'react'
import styled from 'styled-components'

import PageLayout from '@app/components/page_layout'
import { useBulkPatientImport } from '@app/domain/patient/patient.hooks'
import { useListPrograms } from '@app/domain/program/program.hooks'

import { PatientImportErrorsTable } from './patients/patient_import_errors_table'

const FileUploadContainer = styled.div`
  #file-upload-filename {
    width: 25rem;
  }

  .pf-c-button:last-of-type {
    display: none;
  }
`

export const AdminPatientsScreen = () => {
  const [file, setFile] = useState<File | undefined>(undefined)
  const [filename, setFilename] = useState('')
  const [selectedPrograms, setSelectedPrograms] = useState<string[]>([])

  const {
    clearStatus,
    isLoading,
    importErrors,
    numberAdded,
    stage,
    importStatus,
    uploadPatients,
    uploadError,
    uploadStatus,
    numberOfPatientsAddedToPrograms,
  } = useBulkPatientImport()

  const { programs, getPrograms } = useListPrograms()

  const handleClear = useCallback(() => {
    clearStatus()
    setFile(undefined)
    setFilename('')
    setSelectedPrograms([])
  }, [clearStatus])

  const handleFileChange = useCallback(
    (
      _event:
        | React.ChangeEvent<HTMLInputElement>
        | React.DragEvent<HTMLElement>,
      inputFile: File,
    ) => {
      clearStatus()
      setFile(inputFile)
      setFilename(inputFile.name)
    },
    [clearStatus],
  )

  const handleSubmit = useCallback(
    async (event) => {
      event.preventDefault()
      uploadPatients(file, selectedPrograms)
    },
    [file, selectedPrograms, uploadPatients],
  )

  const programIdToProgramName = useCallback(
    (programId) => {
      const program = programs.find((p) => p.programId === programId)
      if (program) {
        return program.name
      } else {
        return ''
      }
    },
    [programs],
  )

  const selectedProgramsCounts = (selectedPrograms: string[]) => {
    return selectedPrograms
      .map((programId) => {
        const totalRows = importErrors.length + numberAdded
        const numberAddedToProgram =
          numberOfPatientsAddedToPrograms[programId] ?? 0

        let text = `${numberAddedToProgram} patient${
          numberAddedToProgram !== 1 ? 's were' : ' was'
        } added to program ${programIdToProgramName(programId)}`

        const alreadyEnrolled = totalRows - numberAddedToProgram
        if (alreadyEnrolled > 0) {
          text = `${text}, ${alreadyEnrolled} patient${
            alreadyEnrolled !== 1 ? 's were' : ' was'
          } already enrolled.`
        }
        return text
      })
      .sort((a, b) => a.localeCompare(b))
  }

  const generateImportFailureMessage = () => {
    const messages = []

    messages.push(
      `${numberAdded} patient${numberAdded !== 1 ? 's' : ''} added.`,
    )

    if (importErrors.length > 0) {
      messages.push(
        `The system failed to import ${importErrors.length} patient${
          importErrors.length !== 1 ? 's' : ''
        }.`,
      )
    }

    if (selectedPrograms.length > 0) {
      messages.push(...selectedProgramsCounts(selectedPrograms))
    }

    return (
      <ul style={{ listStyleType: 'disc', marginLeft: '20px' }}>
        {messages.map((message, index) => (
          <li key={index}>{message}</li>
        ))}
      </ul>
    )
  }

  const generateImportSucceededMessage = () => {
    const messages = []

    messages.push(
      `${numberAdded} patient${numberAdded !== 1 ? 's' : ''} added.`,
    )

    return (
      <ul style={{ listStyleType: 'disc', marginLeft: '20px' }}>
        {messages.map((message, index) => (
          <li key={index}>{message}</li>
        ))}
      </ul>
    )
  }

  React.useEffect(() => {
    getPrograms()
  }, [getPrograms])

  return (
    <PageLayout>
      <PageSection>
        <Title headingLevel="h1" size="xl">
          Patient Bulk Import
        </Title>
        {uploadStatus === 'failure' ? (
          <Alert
            variant={'warning'}
            title={uploadError}
            aria-live="polite"
            isInline
          />
        ) : null}
        <Form onSubmit={handleSubmit}>
          <Flex>
            <FlexItem>
              <FormGroup label="Import File" isRequired fieldId="file-upload">
                <FileUploadContainer>
                  <FileUpload
                    id="file-upload"
                    filename={filename}
                    isLoading={isLoading}
                    onClearClick={handleClear}
                    browseButtonText="Browse"
                    clearButtonText="Clear"
                    hideDefaultPreview
                    onFileInputChange={handleFileChange}
                  />
                </FileUploadContainer>
              </FormGroup>
            </FlexItem>
          </Flex>

          <FormGroup>
            <Button
              isDisabled={file === undefined || isLoading}
              type="submit"
              variant="primary"
            >
              Upload
            </Button>
            <Button type="reset" variant="secondary" onClick={handleClear}>
              Reset
            </Button>
          </FormGroup>
        </Form>
      </PageSection>
      <PageSection variant={PageSectionVariants.light} isFilled={true}>
        {stage === 'validation' && importStatus === 'failure' ? (
          <Alert
            variant="warning"
            title={`No patients were added due to import file errors.`}
            aria-live="polite"
            isInline
          />
        ) : null}

        {stage === 'import' && importStatus === 'failure' ? (
          <Alert
            variant="warning"
            title={generateImportFailureMessage()}
            aria-live="polite"
            isInline
          />
        ) : null}

        {stage === 'import' && importStatus === 'success' ? (
          <Alert
            variant="info"
            title={generateImportSucceededMessage()}
            aria-live="polite"
            isInline
          />
        ) : null}

        {importErrors.length > 0 ? (
          <PatientImportErrorsTable errors={importErrors} />
        ) : null}
      </PageSection>
    </PageLayout>
  )
}
